import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '..';
import { generalApi } from '../../api';
import {
  GiftCard as IGiftCard,
  IAddress,
  IExcludeLocationPayload,
  IExcludedLocation,
  ILocation,
  ILocationAvailabilityPayload,
  ILocationAvailabilityResponse,
} from '../../types';

export interface GeneralState {
  loading: boolean;
  addresses: IAddress[];
  locations: ILocation[];
  giftCards: IGiftCard[];
  excludedLocations: IExcludedLocation[];
  locationAvailability: ILocationAvailabilityResponse | null;
  error: string;
}

const initialState: GeneralState = {
  loading: false,
  addresses: [],
  locations: [],
  giftCards: [],
  excludedLocations: [],
  locationAvailability: null,
  error: '',
};

export const generalSlice = createSlice({
  name: 'general',
  initialState,
  reducers: {
    clearLocationAvailability: (state) => {
      state.locationAvailability = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAllAddresses.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getAllAddresses.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.addresses = payload;
    });
    builder.addCase(getAllAddresses.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message as string;
    });

    builder.addCase(getLocations.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getLocations.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.locations = payload;
    });
    builder.addCase(getLocations.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message as string;
    });

    builder.addCase(getGiftCards.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getGiftCards.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.giftCards = payload;
    });
    builder.addCase(getGiftCards.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message as string;
    });

    builder.addCase(getExcludedLocations.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getExcludedLocations.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.excludedLocations = payload;
    });
    builder.addCase(getExcludedLocations.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message as string;
    });

    builder.addCase(checkLocationAvailability.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      checkLocationAvailability.fulfilled,
      (state, { payload }) => {
        state.loading = false;
        state.locationAvailability = payload;
      }
    );
    builder.addCase(checkLocationAvailability.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message as string;
    });
  },
});

const getAllAddresses = createAsyncThunk('getAllAddresses', async () => {
  try {
    const { data } = await generalApi.getAllAddresses();
    return data.data;
  } catch (error: any) {
    throw error;
  }
});

const getLocations = createAsyncThunk('getLocations', async () => {
  try {
    const { data } = await generalApi.getLocations();
    return data.data;
  } catch (error: any) {
    throw error;
  }
});

const getGiftCards = createAsyncThunk('getGiftCards', async () => {
  try {
    const { data } = await generalApi.getGiftCards();
    return data.data;
  } catch (error: any) {
    throw error;
  }
});

const getExcludedLocations = createAsyncThunk(
  'getExcludedLocations',
  async (params: { brandId?: number; branchId?: string }) => {
    try {
      const { data } = await generalApi.getExcludedLocations(params);
      return data.data;
    } catch (error: any) {
      throw error;
    }
  }
);

const excludeLocation = createAsyncThunk(
  'excludeLocation',
  async (payload: IExcludeLocationPayload, { dispatch }) => {
    try {
      const { data } = await generalApi.excludeLocation(payload);
      // Refresh the excluded locations list
      if (payload.brandId) {
        dispatch(getExcludedLocations({ brandId: payload.brandId }));
      } else if (payload.branchId) {
        dispatch(
          getExcludedLocations({
            branchId: payload.branchId,
            brandId: payload.brandId,
          })
        );
      }
      return data.data;
    } catch (error: any) {
      throw error;
    }
  }
);

const removeExcludedLocation = createAsyncThunk(
  'removeExcludedLocation',
  async (
    params: { id: number; brandId?: number; branchId?: string },
    { dispatch }
  ) => {
    try {
      const { id, brandId, branchId } = params;
      const { data } = await generalApi.removeExcludedLocation(id);
      // Refresh the excluded locations list
      if (brandId) {
        dispatch(getExcludedLocations({ brandId }));
      } else if (branchId) {
        dispatch(getExcludedLocations({ branchId }));
      }
      return data.data;
    } catch (error: any) {
      throw error;
    }
  }
);

const checkLocationAvailability = createAsyncThunk(
  'checkLocationAvailability',
  async (payload: ILocationAvailabilityPayload) => {
    try {
      const { data } = await generalApi.checkLocationAvailability(payload);
      return data.data;
    } catch (error: any) {
      throw error;
    }
  }
);

export const generalSelector = (state: RootState) => state.general;

export const { clearLocationAvailability } = generalSlice.actions;

export {
  getAllAddresses,
  getLocations,
  getGiftCards,
  getExcludedLocations,
  excludeLocation,
  removeExcludedLocation,
  checkLocationAvailability,
};

export default generalSlice.reducer;
